/**
 * VendorLogoCarousel — Iter 67aa.
 *
 * Sits under the hero stepper search on the home page and showcases the trusted
 * hosts on the platform as a softly auto-scrolling marquee of brand logos.
 * When the renter picks a country in the stepper, the carousel filters to
 * vendors who have at least one active car listed in that country, so the row
 * becomes a real-time "you'll be renting from these local partners" reassurance.
 *
 * Implementation notes:
 *   - Backend feed: `GET /api/vendors/logos?country=…` (no auth, paginated).
 *   - Auto-scrolls horizontally via a CSS keyframes marquee. Duplicates the
 *     vendor list once so the loop seam is invisible. Pauses on hover.
 *   - Logos link through to the vendor's public policies page so curious
 *     renters can read the host's T&Cs before booking.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Building2 } from "lucide-react";
import { recordVendorVisit } from "@/lib/recentVendors";

export default function VendorLogoCarousel({ country }) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrored(false);
    api
      .get("/vendors/logos", { params: country ? { country } : {} })
      .then((r) => { if (!cancelled) setVendors(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setErrored(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [country]);

  // Hide the whole strip when the API errors — it's a "nice to have", not critical UX.
  if (errored) return null;

  // While the first network request is in flight, render a thin skeleton row so
  // the layout doesn't jump as the data lands.
  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-2" data-testid="vendor-logo-carousel-loading">
        <div className="rounded-3xl border border-border bg-white/60 backdrop-blur px-5 py-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-3 w-32 rounded-full bg-neutral-100 animate-pulse" />
            <div className="h-2 flex-1 rounded-full bg-neutral-50" />
          </div>
          <div className="flex gap-8 overflow-hidden">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-12 w-32 rounded-xl bg-neutral-100 animate-pulse shrink-0" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  const empty = vendors.length === 0;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-2" data-testid="vendor-logo-carousel">
      <div className="rounded-3xl border border-border bg-gradient-to-br from-white via-sky-50/30 to-indigo-50/30 backdrop-blur px-5 py-6 shadow-sm">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shadow-sm shrink-0">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500">Trusted hosts</div>
              <div className="font-display text-base sm:text-lg font-semibold text-neutral-900" data-testid="vendor-logo-carousel-title">
                {country ? <>Local partners in <span className="text-sky-700">{country}</span></> : "Our partner hosts across Albania"}
              </div>
            </div>
          </div>
          <div className="text-[11px] text-neutral-500 hidden sm:block" data-testid="vendor-logo-carousel-count">
            {empty ? "No partners yet" : `${vendors.length} host${vendors.length === 1 ? "" : "s"}`}
          </div>
        </div>

        {empty ? (
          <div className="rounded-2xl border border-dashed border-border bg-white/60 px-4 py-6 text-center text-sm text-neutral-500" data-testid="vendor-logo-carousel-empty">
            No partner hosts listed in <span className="font-medium text-neutral-700">{country || "this region"}</span> yet — be the first to list a car here.
          </div>
        ) : (
          <VendorMarquee vendors={vendors} />
        )}
      </div>
    </section>
  );
}

function VendorMarquee({ vendors }) {
  // Duplicate the list so the marquee loop has no visible seam. Each loop pass
  // gets a unique React key prefix so the two copies don't collide.
  const loops = [
    { prefix: "a", items: vendors },
    { prefix: "b", items: vendors },
  ];

  // Adapt the animation duration to the vendor count so a row of 3 logos doesn't
  // sprint and a row of 20 doesn't crawl. ~3.2s per logo feels natural.
  const duration = Math.max(18, vendors.length * 3.2);

  return (
    <div className="relative overflow-hidden group" data-testid="vendor-logo-carousel-marquee">
      {/* Edge fade so the marquee blends into the card rather than jumping at the edges. */}
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent z-10" />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent z-10" />

      <div
        className="flex w-max items-center gap-3 animate-vendor-marquee group-hover:[animation-play-state:paused]"
        style={{ animationDuration: `${duration}s` }}
      >
        {loops.map(({ prefix, items }) =>
          items.map((v) => (
            <VendorLogoTile key={`${prefix}-${v.id}`} vendor={v} />
          ))
        )}
      </div>
    </div>
  );
}

function VendorLogoTile({ vendor }) {
  const [broken, setBroken] = useState(false);
  const src = fileUrl(vendor.logo);

  return (
    <Link
      to={`/cars?vendor_id=${encodeURIComponent(vendor.id)}`}
      onClick={() => recordVendorVisit({ id: vendor.id, name: vendor.name, logo: vendor.logo })}
      className="group/tile shrink-0 h-16 sm:h-20 px-5 sm:px-7 rounded-2xl bg-white border border-border hover:border-sky-300 hover:shadow-md transition-all duration-300 flex items-center justify-center gap-3 min-w-[140px] sm:min-w-[180px]"
      title={`See ${vendor.name}'s vehicles`}
      data-testid={`vendor-logo-tile-${vendor.id}`}
    >
      {!broken && src ? (
        <img
          src={src}
          alt={vendor.name}
          className="max-h-10 sm:max-h-12 max-w-[120px] sm:max-w-[140px] w-auto object-contain grayscale group-hover/tile:grayscale-0 opacity-80 group-hover/tile:opacity-100 transition"
          onError={() => setBroken(true)}
          loading="lazy"
        />
      ) : (
        <span className="font-display text-sm sm:text-base font-semibold text-neutral-700 truncate">
          {vendor.name}
        </span>
      )}
    </Link>
  );
}
